import Link from 'next/link'
import React, { HTMLAttributes, forwardRef } from 'react'

import { VariantProps, cva } from 'class-variance-authority'
import clsx from 'clsx'

import { Spinner } from '../Spinner/Spinner'

export interface CustomButtonProps extends HTMLAttributes<HTMLButtonElement & HTMLAnchorElement> {
  /**
   * If `true`, the button will show a spinner.
   */
  loading?: boolean
  /**
   * The html button type to use.
   */
  type?: 'button' | 'reset' | 'submit'
  /**
   * Href for button
   */
  href?: string
  /**
   * Href target
   */
  hrefTarget?: '_blank' | '_self'
  /**
   * Shimming target
   */
  target?: string
  /**
   * Shimming ref as optional prop
   */
  ref?: React.ForwardedRef<HTMLButtonElement>
  /**
   * Shimming rel
   */
  rel?: string
  testId?: string
  children?: any
  preserveTextWhileLoading?: boolean
  spinnerColor?: 'primary' | 'white' | 'black'
}

export type ButtonProps = CustomButtonProps & VariantProps<typeof buttonVariants>

export const buttonVariants = cva(
  'border text-center cursor-pointer items-center font-body select-none md:whitespace-nowrap align-middle outline-0 text-sm hover:no-underline transition rounded-full font-medium',
  {
    defaultVariants: {
      disabled: false,
      fullWidth: false,
      size: 'medium',
      theme: 'primary'
    },
    variants: {
      // TODO: make the disabled styles work
      disabled: {
        false: '',
        true: 'pointer-events-none !cursor-not-allowed opacity-disabled'
      },
      fullWidth: {
        false: 'w-auto',
        true: 'w-full'
      },
      size: {
        medium: 'px-4 py-3',
        small: 'px-2 py-2'
      },
      theme: {
        danger:
          'hover:bg-error-default-hover active:bg-error-default-pressed hover:text-white active:text-white active:shadow-inner border-error-default text-error-default',
        inverse: 'bg-transparent text-text-default border-text-default',
        primary:
          'hover:shadow-primary  bg-button-default border-button-default hover:text-text-default text-text-default hover:bg-button-hover active:bg-button-pressed',
        secondary:
          'border-primary-default bg-inherit text-primary-default hover:text-black  active:text-black  hover:bg-primary-default-hover active:bg-primary-default-pressed',
        /*
         * A button without borders
         */
        tertiary: 'underline-offset-4	hover:!underline bg-transparent border-none'
      }
    }
  }
)

/**
 * Button - The Button component is used to trigger an action or event, such as submitting a form, opening a dialog, canceling an action, or performing a delete operation.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    className,
    disabled,
    fullWidth,
    href,
    hrefTarget = '_self',
    loading,
    preserveTextWhileLoading = true,
    rel,
    size = 'medium',
    spinnerColor = 'white',
    testId,
    theme,
    type = 'button',
    ...props
  },
  ref
) {
  if (href)
    return (
      <Link href={href} legacyBehavior passHref>
        <a
          className={clsx(buttonVariants({ disabled, fullWidth, size, theme }), className)}
          data-testid={testId}
          href={href}
          rel={rel}
          target={hrefTarget}
        >
          {children}
        </a>
      </Link>
    )
  else
    return (
      <button
        className={clsx(buttonVariants({ disabled, fullWidth, size, theme }), className)}
        data-loading={loading || undefined}
        data-testid={testId}
        ref={ref}
        type={type}
        {...props}
      >
        {loading ? (
          preserveTextWhileLoading ? (
            <div className='flex w-full justify-center space-x-2'>
              <Spinner color={spinnerColor} />
              <div className='opacity-50'>{children}</div>
            </div>
          ) : (
            <Spinner color={spinnerColor} />
          )
        ) : (
          children
        )}
      </button>
    )
})

Button.displayName = 'Button'
